/**
 * Rate Table Manager (V3)
 *
 * Full CRUD management for DB-backed custom rate tables.
 * System admins can create, edit, delete, and set default rate tables.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface TierData {
  tierMin: number;
  tierMax: number;
  flatRate: number;    // cents
  perSeatRate: number; // cents
  midpoint?: number;
  tierNumber?: number;
}

interface RateTable {
  id: string;
  name: string;
  description: string | null;
  tiers: TierData[];
  isDefault: boolean;
  displayOrder: number;
}

interface TierInput {
  flatRate: string;    // dollar string for input
  perSeatRate: string; // dollar string for input
}

interface BillingPlansManagerProps {
  token: string;
}

const DEFAULT_TIERS: TierInput[] = Array.from({ length: 10 }, () => ({
  flatRate: '',
  perSeatRate: '',
}));

const getTierRange = (tierIndex: number): { min: number; max: number } => ({
  min: tierIndex * 10 + 1,
  max: (tierIndex + 1) * 10,
});

export const BillingPlansManager: React.FC<BillingPlansManagerProps> = ({ token }) => {
  const [rateTables, setRateTables] = useState<RateTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState<RateTable | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalTiers, setModalTiers] = useState<TierInput[]>(DEFAULT_TIERS);
  const [saving, setSaving] = useState(false);

  const config = { headers: { Authorization: `Bearer ${token}` } };

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchRateTables = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_BASE_URL}/api/system-admin/billing/rate-tables`, config);
      setRateTables(res.data.rateTables || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch rate tables';
      const axiosMsg = (err as any)?.response?.data?.message;
      setError(axiosMsg || msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRateTables();
  }, [fetchRateTables]);

  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  const openCreateModal = (copyFrom?: RateTable) => {
    setEditingTable(null);
    setModalName('');
    setModalDescription('');
    // Pre-fill from an existing table (default: first/default table) so user starts with valid values
    const source = copyFrom || rateTables.find(t => t.isDefault) || rateTables[0];
    if (source) {
      setModalTiers(Array.from({ length: 10 }, (_, i) => {
        const tier = source.tiers.find(t => t.tierMin === i * 10 + 1);
        return {
          flatRate: tier ? (tier.flatRate / 100).toString() : '',
          perSeatRate: tier ? (tier.perSeatRate / 100).toString() : '',
        };
      }));
    } else {
      setModalTiers(DEFAULT_TIERS.map(() => ({ flatRate: '', perSeatRate: '' })));
    }
    setShowModal(true);
  };

  const openEditModal = (table: RateTable) => {
    setEditingTable(table);
    setModalName(table.name);
    setModalDescription(table.description || '');
    // Convert cents to dollar strings for tier inputs
    const tierInputs: TierInput[] = Array.from({ length: 10 }, (_, i) => {
      const tier = table.tiers.find(t => t.tierMin === i * 10 + 1);
      if (tier) {
        return {
          flatRate: (tier.flatRate / 100).toString(),
          perSeatRate: (tier.perSeatRate / 100).toString(),
        };
      }
      return { flatRate: '', perSeatRate: '' };
    });
    setModalTiers(tierInputs);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTable(null);
  };

  const updateTier = (index: number, field: 'flatRate' | 'perSeatRate', value: string) => {
    // Allow empty, digits, and one decimal point
    if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setModalTiers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-derive per-seat rate from flat rate: flatRate / tierMax
      if (field === 'flatRate' && value) {
        const flatDollars = parseFloat(value);
        if (!isNaN(flatDollars) && flatDollars > 0) {
          const tierMax = (index + 1) * 10;
          const perSeat = flatDollars / tierMax;
          updated[index].perSeatRate = perSeat % 1 === 0 ? perSeat.toString() : perSeat.toFixed(2);
        }
      }
      return updated;
    });
  };

  const handleSave = async () => {
    if (!modalName.trim()) {
      showToast('error', 'Name is required');
      return;
    }

    // Validate all tiers have values
    const emptyTiers = modalTiers
      .map((tier, i) => (!tier.flatRate || !tier.perSeatRate) ? i + 1 : null)
      .filter(Boolean);
    if (emptyTiers.length > 0) {
      showToast('error', `All 10 tiers require both Flat Rate and Per-Seat Rate. Missing: Tier ${emptyTiers.join(', ')}`);
      return;
    }

    // Build tiers array, converting dollars to cents
    const tiers = modalTiers.map((tier, i) => {
      const range = getTierRange(i);
      const flatDollars = parseFloat(tier.flatRate);
      const perSeatDollars = parseFloat(tier.perSeatRate);
      return {
        tierMin: range.min,
        tierMax: range.max,
        flatRate: Math.round(flatDollars * 100),
        perSeatRate: Math.round(perSeatDollars * 100),
      };
    });

    try {
      setSaving(true);
      const body = {
        name: modalName.trim(),
        description: modalDescription.trim() || null,
        tiers,
      };

      if (editingTable) {
        await axios.put(
          `${API_BASE_URL}/api/system-admin/billing/rate-tables/${editingTable.id}`,
          body,
          config
        );
        showToast('success', `Rate table "${modalName.trim()}" updated`);
      } else {
        await axios.post(
          `${API_BASE_URL}/api/system-admin/billing/rate-tables`,
          body,
          config
        );
        showToast('success', `Rate table "${modalName.trim()}" created`);
      }

      closeModal();
      await fetchRateTables();
    } catch (err: unknown) {
      const axiosMsg = (err as any)?.response?.data?.error || (err as any)?.response?.data?.message;
      const msg = axiosMsg || (err instanceof Error ? err.message : 'Failed to save rate table');
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (table: RateTable) => {
    if (!window.confirm(`Delete rate table "${table.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/system-admin/billing/rate-tables/${table.id}`,
        config
      );
      showToast('success', `Rate table "${table.name}" deleted`);
      await fetchRateTables();
    } catch (err: unknown) {
      const axiosMsg = (err as any)?.response?.data?.error || (err as any)?.response?.data?.message;
      const msg = axiosMsg || (err instanceof Error ? err.message : 'Failed to delete rate table');
      showToast('error', msg);
    }
  };

  const handleSetDefault = async (table: RateTable) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/rate-tables/${table.id}/set-default`,
        {},
        config
      );
      showToast('success', `"${table.name}" set as default rate table`);
      await fetchRateTables();
    } catch (err: unknown) {
      const axiosMsg = (err as any)?.response?.data?.error || (err as any)?.response?.data?.message;
      const msg = axiosMsg || (err instanceof Error ? err.message : 'Failed to set default');
      showToast('error', msg);
    }
  };

  const getTierSummary = (tiers: TierData[]): string => {
    if (!tiers || tiers.length === 0) return 'No tiers';
    const first = tiers[0];
    const last = tiers[tiers.length - 1];
    const firstLabel = `Tier 1: ${formatCurrency(first.flatRate)}`;
    const lastLabel = `Tier ${tiers.length}: ${formatCurrency(last.flatRate)}`;
    return `${firstLabel} ... ${lastLabel}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800">
        <p>{error}</p>
        <button
          onClick={fetchRateTables}
          className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            toast.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Pricing Rate Tables
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage rate tables used for subscription billing. Each table defines 10 tiers of seat-based pricing.
          </p>
        </div>
        <button
          onClick={() => openCreateModal()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm"
        >
          Create Rate Table
        </button>
      </div>

      {/* Midpoint Rule Explanation */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Midpoint Snap Rule
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li>
            For each tier, the <strong>midpoint</strong> = floor((tierMin + tierMax) / 2) + 1
          </li>
          <li>
            If the license count is <strong>at or above</strong> the midpoint (or in the first tier), the company pays the <strong>full flat rate</strong> for that tier
          </li>
          <li>
            If the license count is <strong>below</strong> the midpoint, the company pays the <strong>previous tier's flat rate</strong> plus overflow seats at the current tier's per-seat rate
          </li>
          <li>
            All license changes take effect on the <strong>next billing cycle</strong> (no mid-cycle proration)
          </li>
        </ul>
      </div>

      {/* Rate Table Cards */}
      {rateTables.length === 0 ? (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow">
          <p className="text-gray-500 dark:text-gray-400">No rate tables created yet.</p>
          <button
            onClick={() => openCreateModal()}
            className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
          >
            Create Your First Rate Table
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {rateTables.map((table) => (
            <div
              key={table.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-5"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      {table.name}
                    </h4>
                    {table.isDefault && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        Default
                      </span>
                    )}
                  </div>
                  {table.description && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {table.description}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 font-mono">
                    {getTierSummary(table.tiers)}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  {!table.isDefault && (
                    <button
                      onClick={() => handleSetDefault(table)}
                      className="px-3 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 dark:text-blue-300 rounded transition-colors"
                    >
                      Set as Default
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(table)}
                    className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 rounded transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(table)}
                    disabled={table.isDefault}
                    className="px-3 py-1.5 text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title={table.isDefault ? 'Cannot delete the default rate table' : 'Delete rate table'}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Expanded tier table */}
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Range
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Midpoint
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Flat Rate
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Per-Seat Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {table.tiers.map((tier, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                          {tier.tierNumber ?? idx + 1}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {tier.tierMin}-{tier.tierMax} licenses
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {tier.midpoint ?? Math.floor((tier.tierMin + tier.tierMax) / 2) + 1}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                          {formatCurrency(tier.flatRate)}/mo
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">
                          {formatCurrency(tier.perSeatRate)}/seat
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeModal}
          />

          {/* Modal content */}
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingTable ? 'Edit Rate Table' : 'Create Rate Table'}
              </h3>
            </div>

            <div className="p-6 space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  placeholder="e.g. Standard, Partner, Enterprise"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={modalDescription}
                  onChange={(e) => setModalDescription(e.target.value)}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Copy From (create mode only) */}
              {!editingTable && rateTables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Copy pricing from
                  </label>
                  <select
                    onChange={(e) => {
                      const source = rateTables.find(t => t.id === e.target.value);
                      if (source) {
                        setModalTiers(Array.from({ length: 10 }, (_, i) => {
                          const tier = source.tiers.find(t => t.tierMin === i * 10 + 1);
                          return {
                            flatRate: tier ? (tier.flatRate / 100).toString() : '',
                            perSeatRate: tier ? (tier.perSeatRate / 100).toString() : '',
                          };
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {rateTables.map(t => (
                      <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (default)' : ''}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    All 10 tiers are required. Start from an existing table and adjust the prices you want to change.
                  </p>
                </div>
              )}

              {/* Tier Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tier Pricing (enter amounts in dollars)
                </label>
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16">
                          Tier
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-32">
                          Range
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Flat Rate ($)
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Per-Seat Rate ($)
                          <span className="normal-case font-normal text-gray-400 ml-1">(auto)</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {modalTiers.map((tier, idx) => {
                        const range = getTierRange(idx);
                        return (
                          <tr key={idx}>
                            <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                              {idx + 1}
                            </td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                              {range.min}-{range.max}
                            </td>
                            <td className="px-3 py-2">
                              <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-400 text-xs">$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={tier.flatRate}
                                  onChange={(e) => updateTier(idx, 'flatRate', e.target.value)}
                                  placeholder="0"
                                  className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-400 text-xs">$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={tier.perSeatRate}
                                  onChange={(e) => updateTier(idx, 'perSeatRate', e.target.value)}
                                  placeholder="auto"
                                  className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-700"
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !modalName.trim()}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingTable ? 'Update Rate Table' : 'Create Rate Table'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
